import React, { useEffect } from 'react';

import { Box, COLORS, Text } from '@clutter/clean';

import { AddressFragment } from '@graphql/platform';

import { RadioCard } from '@root/components/checkout/product_pages/subcomponents/radio_card';
import { MovingCheckoutStepProps } from '@root/components/checkout/types';
import { useTrack } from '@root/initializers/wt';
import { EventSchema } from '@root/resources/wt/types';

import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';

import calendarImg from '@images/illustrations/calendar.svg';
import clockImg from '@images/illustrations/clock_large.svg';

import { CardContents } from '../../subcomponents/card_contents';

export const DESKTOP_PLAN_SELECTOR_SIZE = '356px'; // card height * 3 + gap * 2

type DeliveryStepProps = MovingCheckoutStepProps & {
  movingExtendedDestinationServiceArea?: boolean;
  endAddress?: Partial<AddressFragment>;
  longDistance?: boolean;
};

export const Delivery: React.FC<DeliveryStepProps> = ({
  onChange,
  values: { scheduleDeliverySelected, currentStorageCustomer, movingQuote },
  movingExtendedDestinationServiceArea,
}) => {
  const track = useTrack({ action: 'click' });
  const trackFunnelEvent = useTrackFunnelEvents();

  const longDistance = !!movingQuote?.longDistance;

  const visible =
    !currentStorageCustomer &&
    !movingExtendedDestinationServiceArea &&
    longDistance;

  useEffect(() => {
    visible &&
      trackFunnelEvent({
        schema: EventSchema.WWW__DeliveryOptionViewed,
        action: 'display',
        metadata: {},
      });
  }, [visible, trackFunnelEvent]);

  useEffect(() => {
    if (!longDistance) {
      onChange('scheduleDeliverySelected', undefined);
    }
  }, [longDistance]);

  if (!visible) {
    return null;
  }

  return (
    <Box margin={['48px 0 0', null, '72px 0 0']}>
      <Box>
        <Text.Title size="small" color={COLORS.tealDark}>
          Do you want a guaranteed delivery date?
        </Text.Title>
      </Box>
      <Box margin="4px 0 0">
        <Text.Body color={COLORS.storm}>
          Looks like you are moving long-distance! You can take advantage of our
          full-storage solution to get your items delivered at a later date.
        </Text.Body>
      </Box>
      <Box>
        <Box.Grid
          margin={['24px 0 0', '32px 0 0']}
          gridTemplateColumns={['1fr', '1fr 1fr']}
          gridGap={['12px', '16px']}
        >
          <RadioCard
            name="deliverySelection"
            value="true"
            selected={scheduleDeliverySelected === true}
            onChange={() => {
              track({
                objectName: 'delivery_selection_card',
                objectType: 'button',
                label: 'Yes, schedule my delivery now',
                value: true,
              });
              trackFunnelEvent({
                schema: EventSchema.WWW__DeliveryOptionCompleted,
                action: 'click',
                metadata: { selected: true },
              });
              onChange('scheduleDeliverySelected', true);
              onChange('unpackingScheduled', undefined);
            }}
          >
            <CardContents
              testId="schedule_delivery_now"
              label="Yes, schedule my delivery now"
              imgSrc={calendarImg}
              description={
                <>
                  We will deliver your belongings to your destination by your
                  guaranteed date.
                </>
              }
              price={null}
            />
          </RadioCard>
          <RadioCard
            name="deliverySelection"
            value="false"
            selected={scheduleDeliverySelected === false}
            onChange={() => {
              track({
                objectName: 'delivery_selection_card',
                objectType: 'button',
                label: 'Schedule my delivery at a later date',
                value: false,
              });
              trackFunnelEvent({
                schema: EventSchema.WWW__DeliveryOptionCompleted,
                action: 'click',
                metadata: { selected: false },
              });
              onChange('scheduleDeliverySelected', false);
              onChange('unpackingScheduled', undefined);
            }}
          >
            <CardContents
              testId="schedule_delivery_later"
              label="Schedule my delivery at a later date"
              imgSrc={clockImg}
              description="We will safely store your items until you are ready for them."
              price={null}
            />
          </RadioCard>
        </Box.Grid>
      </Box>
    </Box>
  );
};
